import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as teamService from '@/services/teamService';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * Query key factory for teams
 */
export const teamKeys = {
  all: ['teams'] as const,
  memberships: (userId: string) => ['teams', 'memberships', userId] as const,
  byId: (teamId: string) => ['teams', teamId] as const,
  members: (teamId: string) => ['teams', teamId, 'members'] as const,
  activeTeam: (userId: string) => ['teams', 'active', userId] as const,
};

/**
 * Hook to fetch user's team memberships
 */
export function useUserMemberships() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: teamKeys.memberships(user?.id || ''),
    queryFn: () => teamService.getUserMemberships(user!.id),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes - team memberships don't change often
  });
}

/**
 * Hook to fetch a specific team by ID
 */
export function useTeamById(teamId: string | null) {
  return useQuery({
    queryKey: teamKeys.byId(teamId || ''),
    queryFn: () => teamService.getTeamById(teamId!),
    enabled: !!teamId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch team members
 */
export function useTeamMembers(teamId: string | null) {
  return useQuery({
    queryKey: teamKeys.members(teamId || ''),
    queryFn: () => teamService.getTeamMembers(teamId!),
    enabled: !!teamId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get active team from profile
 */
export function useActiveTeam() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: teamKeys.activeTeam(user?.id || ''),
    queryFn: async () => {
      if (!user?.id) return null;
      
      // Get profile to find active team
      const { data, error } = await supabase
        .from('profiles')
        .select('active_team_id')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      
      if (!data?.active_team_id) return null;
      
      return teamService.getTeamById(data.active_team_id);
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 1, // 1 minute - active team might change
  });
}

/**
 * Hook to switch active team
 */
export function useSwitchTeam() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: teamService.setActiveTeam,
    onSuccess: (_, teamId) => {
      // Invalidate queries that depend on active team
      queryClient.invalidateQueries({ queryKey: teamKeys.activeTeam(user?.id || '') });
      queryClient.invalidateQueries({ queryKey: ['activities'] }); // Activities depend on team
      queryClient.invalidateQueries({ queryKey: ['deliverables'] }); // Deliverables depend on team
    },
  });
}

/**
 * Check if user is admin of a team
 */
export function useIsTeamAdmin(teamId?: string) {
  const { user } = useAuth();
  const { data: memberships } = useUserMemberships();
  
  if (!teamId || !memberships) return false;
  
  return teamService.isUserTeamAdmin(memberships, teamId);
}
